import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, postMultipart, del, put } from 'api/fetch';
import { cleanData } from '../helpers';
import { CLAIM_TYPES } from '../consts';

const getClaimTypeString = (claimType) =>
  claimType === CLAIM_TYPES.TRAVEL ? 'TRAVEL' : 'MEDICAL';

export const initiateClaim = createAsyncThunk(
  'nomad_insurance/claim/initiateClaim',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { claimant, claimInfo, claimType, bankAccount } = state.nomadInsurance.claims;
    const { userID } = state.app.user;

    const { description, preExistingCondition, claimFiles, otherParties, ...rest } =
      claimInfo;

    const url = '/claims/v6/initiate';
    try {
      const { id: claimID } = await post(
        url,
        {
          ...rest,
          bankingDetailsID: bankAccount?.id,
          claimType: getClaimTypeString(claimType),
          happenedComment: description,
          startComment: preExistingCondition,
          otherPartiesComment: otherParties,
          groupMemberID: claimant === userID ? undefined : claimant
        },
        {
          isNIOrchestrator: true
        }
      );

      return claimID;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editClaim = createAsyncThunk(
  'nomad_insurance/claim/editClaim',
  async ({ claimID, editedInfo }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const { claimant, claimInfo, claimType } = state.nomadInsurance.claims;
    const { userID } = state.app.user;

    const { description, preExistingCondition, claimFiles, otherParties, ...rest } =
      claimInfo;
    const {
      description: editedDescription = '',
      otherParties: editedOtherParties = '',
      preExistingCondition: editedPreExistingCondition = '',
      ...restEdited
    } = editedInfo || {};

    try {
      await put(
        `/claims/v1/${claimID}`,
        {
          ...rest,
          ...restEdited,
          id: claimID,
          claimType: getClaimTypeString(claimType),
          happenedComment: editedDescription || description,
          startComment: editedPreExistingCondition || preExistingCondition,
          otherPartiesComment: editedOtherParties || otherParties,
          groupMemberID: claimant === userID ? undefined : claimant
        },
        {
          isNIOrchestrator: true
        }
      );
      await dispatch(setClaimInfo(editedInfo)); // eslint-disable-line no-use-before-define
      return claimID;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendFile = createAsyncThunk(
  'nomad_insurance/claim/sendFile',
  async ({ file, claimID }, { rejectWithValue }) => {
    try {
      const { id } = await postMultipart(
        '/claims/files/v1',
        {
          claimId: claimID,
          file,
          category: 'Report',
          fileType: file?.type,
          fileName: file.name
        },
        { isNIOrchestrator: true }
      );
      return { id, name: file.name };
    } catch (error) {
      const parsedError = { name: file.name, ...error };
      return rejectWithValue(parsedError);
    }
  }
);

export const removeFile = createAsyncThunk(
  'nomad_insurance/claim/removeFile',
  async (fileID, { rejectWithValue }) => {
    try {
      await del(`/claims/files/v1/${fileID}`, undefined, {
        isNIOrchestrator: true
      });
      return fileID;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateBankingDetails = createAsyncThunk(
  'nomad_insurance/claim/updateBankingDetails',
  async (info, { rejectWithValue }) => {
    const cleanBankDetails = cleanData(info.bankDetails);

    const payload = {
      displayName: 'default',
      ...cleanBankDetails
    };
    try {
      const response = await post('/claims/v4/bank-details', payload, {
        isNIOrchestrator: true
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const resolveClaimById = async (claimID, { rejectWithValue }) => {
  try {
    const claim = await get(`/claims/v2/${claimID}`, {
      isNIOrchestrator: true
    });
    return { ...claim, claimID };
  } catch (error) {
    return rejectWithValue(error);
  }
};

export const fetchClaimByID = createAsyncThunk(
  'nomad_insurance/claim/fetchClaimByID',
  resolveClaimById
);

export const getSelectedClaim = createAsyncThunk(
  'nomad_insurance/claim/getSelectedClaim',
  resolveClaimById
);

export const fetchContractBasedOnDateOfService = createAsyncThunk(
  'nomad_insurance/claim/fetchContractBasedOnDateOfService',
  async (dateOfService, { rejectWithValue }) => {
    try {
      const { insuranceContractID: contractID } = await get(
        `/contracts/v1/date-of-service?dos=${dateOfService}`,
        {
          isNIOrchestrator: true
        }
      );

      return contractID;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  claimant: '',
  claimType: undefined,
  claimID: null,
  contractID: {
    value: null,
    error: null,
    isLoading: false
  },
  isLoading: false,
  error: null,
  editError: null,
  claimSubmitted: false,
  selectedClaim: null,
  fetchClaimError: null,
  requestStarted: false,
  validSteps: [],
  claimOverThousandUsd: false,
  idCheckNeeded: false,
  idVerified: false,
  documentErrors: [],
  isSendingFiles: [],
  isDeletingFile: false,
  deletingFileID: null,
  bankAccount: null,
  claimInfo: {
    incidentCountry: '',
    incidentCity: '',
    claimAmount: '',
    claimCurrency: 'USD',
    dateOfService: undefined,
    description: '',
    otherParties: '',
    claimFiles: [],
    preExistingCondition: '',
    identityNumber: ''
  }
};

const slice = createSlice({
  name: 'nomad_insurance/claim',
  initialState,
  reducers: {
    addValidStep: (state, { payload }) => {
      if (state.validSteps.length > 0) {
        if (!state.validSteps.find((x) => x === payload)) {
          state.validSteps = [...state.validSteps, payload];
        } else {
          return state;
        }
      } else {
        state.validSteps = [payload];
      }
    },
    removeValidStep: (state, { payload }) => {
      state.validSteps = state.validSteps.filter((x) => x !== payload);
    },
    resetState: () => initialState,
    setClaimId: (state, { payload }) => {
      state.claimID = payload;
    },
    setClaimInfo: (state, { payload }) => {
      state.claimInfo = { ...state.claimInfo, ...payload };
    },
    setClaimant: (state, { payload }) => {
      state.claimant = payload;
    },
    setClaimType: (state, { payload }) => {
      state.claimType = payload;
    },
    resetClaimError: (state) => {
      state.error = null;
    },
    setClaimSubmitted: (state, { payload }) => {
      state.claimSubmitted = payload;
    },
    submitClaimRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    submitClaimSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.claimSubmitted = true;
    },
    submitClaimFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    sendIDVerificationResultRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    sendIDVerificationResultSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    sendIDVerificationResultFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    fetchBankingDetailsRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchBankingDetailsSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.bankAccount = payload;
    },
    fetchBankingDetailsFailure: (state, { payload }) => {
      state.isLoading = false;
      state.bankAccount = null;
      state.error = payload;
    },
    resetBankAccount: (state) => {
      state.bankAccount = null;
    },
    resetRequestState: (state) => {
      state.requestStarted = false;
    },
    setIdCheckNeeded: (state, { payload }) => {
      state.idCheckNeeded = payload;
    },
    setIdVerified: (state, { payload }) => {
      state.idVerified = payload;
    },
    setClaimOverThousandUsd: (state, { payload }) => {
      state.claimOverThousandUsd = payload;
    },
    prefillClaim: (state, { payload }) => {
      const {
        startComment,
        happenedComment,
        otherPartiesComment,
        insuranceContractID,
        id,
        claimStatus,
        claimType,
        ...rest
      } = payload;
      state.claimInfo = rest;
      state.claimID = id;
      state.claimInfo.preExistingCondition = startComment;
      state.claimInfo.description = happenedComment;
      state.claimInfo.otherParties = otherPartiesComment;
      state.requestStarted = false;
    }
  },
  extraReducers: {
    [initiateClaim.pending]: (state) => {
      state.requestStarted = true;
      state.isLoading = true;
      state.error = null;
    },
    [initiateClaim.fulfilled]: (state, { payload }) => {
      state.claimID = payload;
      state.isLoading = false;
      state.error = null;
    },
    [initiateClaim.rejected]: (state, { payload }) => {
      state.error = payload;
      state.isLoading = false;
      state.requestStarted = false;
    },
    [editClaim.pending]: (state) => {
      state.requestStarted = true;
      state.isLoading = true;
      state.editError = null;
    },
    [editClaim.fulfilled]: (state, { payload }) => {
      state.claimID = payload;
      state.isLoading = false;
      state.editError = null;
    },
    [editClaim.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.editError = payload;
      state.requestStarted = false;
    },
    [sendFile.pending]: (state, { meta }) => {
      state.isLoading = true;
      state.isSendingFiles = [...state.isSendingFiles, meta.arg.file.name];
    },
    [sendFile.fulfilled]: (state, { payload }) => {
      state.claimInfo.claimFiles = [...state.claimInfo.claimFiles, payload];
      state.isLoading = false;
      state.isSendingFiles = state.isSendingFiles.filter((file) => file !== payload.name);
      state.documentErrors = state.documentErrors.filter(
        (error) => error.fileName !== payload.name
      );
    },
    [sendFile.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.isSendingFiles = state.isSendingFiles.filter((file) => file !== payload.name);
      const error = {
        fileName: payload.name,
        documentError: payload
      };
      state.documentErrors = [error];
    },
    [removeFile.pending]: (state, { meta }) => {
      state.deletingFileID = meta.arg;
      state.isLoading = true;
      state.isDeletingFile = true;
    },
    [removeFile.fulfilled]: (state, { payload }) => {
      state.claimInfo.claimFiles = state.claimInfo.claimFiles.filter(
        (file) => file.id !== payload
      );
      state.isLoading = false;
      state.isDeletingFile = false;
      state.documentErrors = state.documentErrors.filter(
        (error) => error.fileName !== payload.name
      );
    },
    [removeFile.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.isDeletingFile = false;
      const error = {
        fileName: payload.name,
        documentError: payload
      };
      state.documentErrors = [error];
    },
    [updateBankingDetails.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [updateBankingDetails.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.bankAccount = payload;
    },
    [updateBankingDetails.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    [fetchClaimByID.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [fetchClaimByID.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.fetchClaimError = null;
      const {
        accountHolder,
        bankCity,
        bankCode,
        bankCountry,
        bankName,
        iban,
        claimantFirstName,
        claimantLastName,
        reimbursementCurrency,
        claimID,
        startComment,
        happenedComment,
        otherPartiesComment,
        claimType,
        ...claimInfo
      } = payload;
      state.claimID = claimID;
      state.claimType = claimType === 'TRAVEL' ? CLAIM_TYPES.TRAVEL : CLAIM_TYPES.MEDICAL;
      state.claimInfo = claimInfo;
      state.claimInfo.preExistingCondition = startComment;
      state.claimInfo.description = happenedComment;
      state.claimInfo.otherParties = otherPartiesComment;
    },
    [fetchClaimByID.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.claimID = null;
      state.fetchClaimError = payload;
    },
    [getSelectedClaim.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [getSelectedClaim.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.selectedClaim = payload;
    },
    [getSelectedClaim.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
    },
    [fetchContractBasedOnDateOfService.pending]: (state) => {
      state.contractID.isLoading = true;
      state.contractID.error = null;
    },
    [fetchContractBasedOnDateOfService.fulfilled]: (state, { payload }) => {
      state.contractID.isLoading = false;
      state.contractID.error = null;
      state.contractID.value = payload;
    },
    [fetchContractBasedOnDateOfService.rejected]: (state, { payload }) => {
      state.contractID.isLoading = false;
      state.contractID.error = payload;
    }
  }
});

export const {
  addValidStep,
  removeValidStep,
  resetState,
  setClaimId,
  setClaimInfo,
  setClaimant,
  setClaimType,
  setClaimSubmitted,
  resetClaimError,
  resetBankAccount,
  resetRequestState,
  setIdCheckNeeded,
  setIdVerified,
  setClaimOverThousandUsd,
  prefillClaim,
  ...actions
} = slice.actions;

export const submitClaim = (claimID) => async (dispatch) => {
  dispatch(actions.submitClaimRequest());

  try {
    await post(
      '/claims/v1/submit',
      {
        id: claimID
      },
      { isNIOrchestrator: true }
    );
    dispatch(actions.submitClaimSuccess());
  } catch (error) {
    dispatch(actions.submitClaimFailure());
  }
};

export const sendIDVerificationResult = (experienceID, claimID) => async (dispatch) => {
  dispatch(actions.sendIDVerificationResultRequest());

  try {
    await post('/verifications/trulioo-api/embedids/id-verification-job', {
      claimID,
      experienceID
    });
    dispatch(actions.sendIDVerificationResultSuccess());
  } catch (error) {
    dispatch(actions.sendIDVerificationResultFailure());
  }
};

export const getBankingDetails = () => async (dispatch) => {
  dispatch(actions.fetchBankingDetailsRequest());
  try {
    const response = await get('/claims/v4/bank-details/active', {
      isNIOrchestrator: true
    });
    const bankDetails = response;
    if (bankDetails) {
      dispatch(actions.fetchBankingDetailsSuccess(bankDetails));
      return;
    }
    dispatch(actions.fetchBankingDetailsSuccess());
  } catch (error) {
    /**
     * Temporary work around as the backend returns a 500 when there are no bank details
     */
    dispatch(actions.fetchBankingDetailsSuccess());
  }
};

export default slice.reducer;
